<template>
    <div class="security-recommendations-page">
        <div  v-if="loadingAccount">
            <ui-card-skeleton :count="2" class="w-100"/>
        </div>
        <div v-else>
            <span
            class="fas fa-times security-recommendations-page__close-button" 
            @click="handleCancel"
            aria-label="Cancel"
            title="Cancel"
            />
            <div class="security-recommendations-page__header mb-3">
                <h4 class="security-recommendations-page__title">
                    {{ $t('Review your security settings') }}
                </h4>
                <p class="security-recommendations-page__description">
                    {{ $t('Keep your account secure by following these recommendations.') }}
                </p>
            </div>
            <div class="security-recommendations-page__recommendations mb-3" v-if="available_passkeys">
                <div class="recommendation-card">
                    <div class="recommendation-card__header">
                        <h6 class="recommendation-card__title">
                            {{ $t('PASSKEYS') }}
                        </h6>
                        <span v-if="!has_passkeys" class="recommendation-card__badge">
                            {{ $t('Recommended') }}
                        </span>
                        <span v-else class="recommendation-card__badge">
                            {{ $t('Enabled') }}
                        </span>
                    </div>
                    <div class="recommendation-card__body">
                        <p class="recommendation-card__text">
                            {{ $t('Sign in with your fingerprint, facial recognition, or a PIN instead of a password. Passkeys can sync across devices connected to the same platform (like Apple ID or a Google account).') }}
                        </p>
                        <p class="recommendation-card__text">
                            {{ $t('Setup takes less than a minute.') }}
                        </p>
                    </div>
                    <div class="recommendation-card__footer" v-if="!has_passkeys">
                        <ui-button variant="secondary" class="recommendation-card__button" @click="createPasskey" :disabled="save">
                            <i class="fas fa-fingerprint recommendation-card__button-icon"></i>
                            {{ $t('Create a passkey') }}
                        </ui-button>
                    </div>
                </div>
            </div>
            <div class="security-recommendations-page__recommendations mb-3">
                <div class="recommendation-card">
                    <div class="recommendation-card__header">
                        <h6 class="recommendation-card__title">
                            {{ $t('TWO FACTOR AUTHENTICATION') }}
                        </h6>
                        <span v-if="has_2fa" class="recommendation-card__badge">
                            {{ $t('Enabled') }}
                        </span>
                    </div>
                    <div class="recommendation-card__body">
                        <p>
                            {{ $t('You don\'t have two-step authentication enabled. Add an extra layer of security so only you can access your account, even if someone else has your password.') }}
                        </p>
                    </div>
                    <div class="recommendation-card__footer" v-if="!has_2fa">
                        <ui-button 
                            variant="secondary" 
                            class="recommendation-card__button"
                            :disabled="save"
                            @click="activeTwoFactorAuth"
                        >
                            <i class="fas fa-lock recommendation-card__button-icon"></i>
                            {{ $t('Enable Two Factor Authentication') }}
                        </ui-button>
                    </div>
                </div>
            </div>
            <div class="security-recommendations-page__footer mb-2">
                <div class="security-recommendations-page__actions">
                    <ui-button @click="redirectTo" :loading="loading" :disabled="save" class="security-recommendations-page__action-button" type="submit" variant="primary">
                        {{ $t('Continue') }}
                    </ui-button>
                </div>
                <b-button @click="dontShowAgain" :disabled="save" variant="link" type="button" class="security-recommendations-page__no-show-again">
                    <span v-if="loading" class="ui-button__loader fas fa-spinner fa-spin"></span>
                    {{ $t('Don\'t show this again') }}
                </b-button>
            </div>
        </div>
    </div>
</template>


<script>
import UiCardSkeleton from '@/components/ui/CardSkeleton.vue';
import UiCard from '@/components/ui/Card.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import UiSwitch from '@/components/ui/Switch.vue';
import UiBadge from '@/components/ui/badges/Badge.vue';

import base64url from 'base64url';  

export default {
    name: 'SecurityRecommendationsPage',
    components: {
        UiCardSkeleton,
        UiCard,
        UiButton,
        UiSwitch,
        UiBadge
    },
    data() {
        return {
            save: false,
            loading: false,
            loadingAccount: false,
            user: null,
            has_2fa: false,
            has_passkeys: false,
            available_passkeys: false,
            message_passkeys: null,
            recommendations: []
        }
    },
    async mounted() {
        this.loadingAccount = true;
        await this.getAccountDetails();
        await this.canActivePassKeys();
        this.loadingAccount = false;
    },
    methods: {
        async getAccountDetails() {
            try {
                const { data } = await this.axiosAccount.get('/api/accounts/me');

                this.user = data;
                this.has_2fa = this.user.two_step_auth?.length > 0;
                this.has_passkeys = this.user.has_biometric_auth;
                if (this.has_2fa && this.has_passkeys || this.user.no_display_tfa) {
                    this.redirectTo();
                }
            } catch(error) {
                this.showError(error);
            }
        },
        async canActivePassKeys() {
            if(window.PublicKeyCredential) {
                const is_available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                if (is_available) {
                    this.available_passkeys = true;
                } else {
                    this.available_passkeys = false;
                    this.message_passkeys = 'Authentication available, but you need to enable biometric or security authentication on your device.';
                }
            } else {
                this.available_passkeys = false;
            }
        },
        async createPasskey() {
            try {
                this.save = true;
                const { data } = await this.axiosAccount.get('/api/biometric-auth/register/options');
                let { options } = data;
                const challenge = this.base64urlToUnit8Array(options.challenge);
                options.challenge = challenge;
                const user = this.base64urlToUnit8Array(options.user.id);
                options.user.id = user;
                const excludeCredentials = options.excludeCredentials.map(cred => {
                    return {
                        ...cred,
                        id: new Uint8Array(base64url.toBuffer(cred.id))
                    };
                });
                options.excludeCredentials = excludeCredentials;
                const credential = await navigator.credentials.create({
                    publicKey: options
                });
                const format_credential = this.format_credential(credential);

                const payload = {
                    id: base64url.encode(credential.rawId),
                    credential: format_credential,
                    attachment: credential.authenticatorAttachment,
                    extensions: credential.getClientExtensionResults(),
                    type: credential.type
                };
                await this.axiosAccount.post('/api/biometric-auth/register', payload);
                this.$toasted.global.successMessage(this.$t('Device successfully registered'));
                //Esperamos un momento y redirigimos
                setTimeout(() => {
                    this.redirectTo();
                }, 3000);
            } catch (error) {
                this.$toasted.global.errorMessage('We could not create the passkey. Please try again later.');
            } finally {
                this.save = false;
            }
        },
        activeTwoFactorAuth() {
            try {
                this.save = true;
                this.$router.push({ name: 'twoFactorAuthentication' });
            } catch (error) {
                this.showError(error);
            } finally {
                this.save = false;
            }
        },
        async redirectTo() {
            const { redirect_url } = this.$route.query;
            if (redirect_url) {
                window.location.replace(redirect_url);
                return;
            }
            this.$router.push({ name: 'dashboard'})
        },
        async dontShowAgain() {
            try {
                await this.axiosAccount.put('/api/accounts/preferences', { no_display_tfa: true });
                this.redirectTo();
            } catch (error) {
                this.showError(error);
            }
        },
        base64urlToUnit8Array(base64urlString) {
            const base64String = base64urlString.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
            const base64Padded = base64String + padding;
            const binaryString = window.atob(base64Padded);
            const byteArray = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
            }
            return byteArray;
        },
        toBase64(str) {
            return btoa(String.fromCharCode(...new Uint8Array(str)));
        },
        format_credential(credential) {
            return {
                clientDataJSON: base64url.encode(credential.response.clientDataJSON),
                attestationObject: base64url.encode(credential.response.attestationObject),
                transports: credential.response.getTransports()            
            };
        },
        async skipForNow() {
            this.cancel = true;
            setTimeout(() => {
                this.redirectTo();
            }, 3000);
        },
        handleCancel() {
            this.redirectTo();
        }
    }
}
</script>

<style lang="scss">
.security-recommendations-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 852px;
    margin: 0 auto;
    position: relative;

    &__close-button {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 1.5rem;
        color: $general-black;
        cursor: pointer;
        padding: 8px;
        transition: color 0.2s ease;

        &:hover {
            color: $accounts-secondary;
        }
    }

    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        text-align: center;
    }

    &__title {
        font-size: 1.5rem;
        font-weight: 600;
        color: $general-black;
        margin-bottom: 0;
    }

    &__description {
        font-size: 1rem;
        font-weight: 400;
        color: $general-black;
        margin-bottom: 0;
    }

    &__recommendations {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    &__footer {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__actions {
        display: flex;
        gap: 12px;
        width: 100%;
    }

    &__action-button {
        flex: 1;
        margin-bottom: 0;
    }

    &__no-show-again {
        color: $accounts-secondary !important;
        font-weight: 500 !important;
        padding: 10px !important;
        text-align: center;
    }
}

.recommendation-card {
    background-color: $white;
    border-radius: 8px;
    border: 0.5px solid $ecart-pay-secondary-200;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
    }

    &__title {
        color: $general-black;
        font-size: 0.875rem;
        font-weight: 600;
        letter-spacing: 0.5px;
        margin-bottom: 0;
        text-transform: uppercase;
    }

    &__badge {
        background-color: $general-success;
        border-radius: 12px;
        color: $white;
        font-size: 0.75rem;
        font-weight: 500;
        padding: 4px 12px;
        white-space: nowrap;
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__text {
        color: $general-black;
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.5;
        margin-bottom: 0;
    }

    &__footer {
        display: flex;
        margin-top: 8px;
    }

    &__button {
        width: 100%;
        justify-content: center;
        gap: 8px;
    }

    &__button-icon {
        font-size: 1rem;
        color: inherit;
    }
}
</style>